import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { Globe, Users, Award, Heart, TrendingUp, Brain, Smile, Calendar, ArrowUp } from 'lucide-react';

interface TeamMetrics {
  productivityImprovement: number;
  mentalHealthImprovement: {
    depression: number;
    anxiety: number;
    stress: number;
  };
  sickLeaveReduction: number;
  employeeSatisfaction: number;
  totalEmployees: number;
  responsesReceived: number;
  socialHarmonyPoints: number;
  teamResilienceScore: number;
  participationRate: number;
}

const mockData: TeamMetrics = {
  productivityImprovement: 23,
  mentalHealthImprovement: {
    depression: 31,
    anxiety: 28,
    stress: 35
  },
  sickLeaveReduction: 42,
  employeeSatisfaction: 87,
  totalEmployees: 250,
  responsesReceived: 218,
  socialHarmonyPoints: 325,
  teamResilienceScore: 82,
  participationRate: 85
};

const TeamDashboard: React.FC = () => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-bold text-gray-800">Team Harmony Dashboard</h2>
        <div className="text-sm text-gray-500">
          Anonymous Contributions: {mockData.responsesReceived} of {mockData.totalEmployees} Team Members
        </div>
      </div>

      {/* Team Harmony Globe (Hero Section) */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Social Harmony Points</h3>
              <div className="text-3xl font-bold text-blue-600">{mockData.socialHarmonyPoints} SHP</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600 mb-1">Team Resilience Score</div>
            <div className="flex items-center justify-end">
              <span className="text-2xl font-bold text-emerald-600">{mockData.teamResilienceScore}/100</span>
              <div className="ml-2 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium flex items-center">
                <ArrowUp className="w-3 h-3 mr-1" />
                5%
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative w-full h-4 bg-blue-100 rounded-full overflow-hidden mb-4">
          <div 
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-indigo-500"
            style={{ width: `${(mockData.socialHarmonyPoints / 500) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Current: {mockData.socialHarmonyPoints} SHP</span>
          <span>Next Goal: 500 SHP</span>
        </div>
      </div>

      {/* Your Anonymous Impact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Users className="w-5 h-5 text-indigo-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Your Anonymous Impact</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Your Contributions</span>
              <span className="font-semibold">12 SHP</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500" style={{ width: '40%' }} />
            </div>
            <button className="w-full py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors">
              Complete Today's Exercise (+2 SHP)
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Award className="w-5 h-5 text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Team Achievements</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center bg-purple-50 rounded-lg p-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <Heart className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <div className="font-medium text-purple-900">Free lunch for 50 families</div>
                <div className="text-sm text-purple-600">Unlocked at 300 SHP</div>
              </div>
            </div>
            <div className="flex items-center bg-blue-50 rounded-lg p-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <Brain className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="font-medium text-blue-900">Mindfulness Workshop</div>
                <div className="text-sm text-blue-600">Next goal: 500 SHP</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Safe Participation Section */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Users className="w-6 h-6 text-emerald-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Safe Participation</h3>
          </div>
          <div className="text-sm font-medium text-emerald-600">
            {mockData.participationRate}% Team Participation
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center text-emerald-700">
              <Users className="w-4 h-4 mr-2" />
              <span>3 teammates completed today's exercise</span>
            </div>
            <button className="mt-3 w-full py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors text-sm">
              Join Anonymously
            </button>
          </div>
          <div className="bg-white bg-opacity-50 rounded-lg p-4">
            <div className="flex items-center text-emerald-700">
              <Heart className="w-4 h-4 mr-2" />
              <span>Your last activity added 1 meal</span>
            </div>
            <div className="mt-2 text-sm text-emerald-600">
              Thank you for your contribution! 🙏
            </div>
          </div>
        </div>
      </div>

      {/* Impact Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center text-blue-500 mb-2">
            <TrendingUp className="w-5 h-5 mr-2" />
            <span className="font-medium">Productivity</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">+{mockData.productivityImprovement}%</div>
          <div className="text-sm text-gray-500">Team improvement</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center text-purple-500 mb-2">
            <Calendar className="w-5 h-5 mr-2" />
            <span className="font-medium">Sick Leave</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">-{mockData.sickLeaveReduction}%</div>
          <div className="text-sm text-gray-500">Reduction in sick days</div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center text-emerald-500 mb-2">
            <Smile className="w-5 h-5 mr-2" />
            <span className="font-medium">Satisfaction</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">{mockData.employeeSatisfaction}%</div>
          <div className="text-sm text-gray-500">Team happiness score</div>
        </div>
      </div>
    </div>
  );
};

export default TeamDashboard;